import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import moment from "moment";
import automationServices from "app/services/automations/automation-services";
import { requestErrors } from "app/services/automations/generated-file-services";

// Cambios a reglas que un asistente (Claude, ChatGPT o un script, por la API o el MCP) propuso y que bajan su protección:
// pausar la regla, subir un umbral, quitar un destinatario… La regla sigue como estaba hasta que alguien con permiso
// para editarla aplica el cambio aquí. Solo aparece si hay alguno pendiente.
const RuleChangeProposals = ({ onApplied }) => {
    const [proposals, setProposals] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const [errors, setErrors] = useState({});

    const load = useCallback(() => {
        automationServices.getRuleChangeProposals().then(setProposals).catch(() => setProposals([]));
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (open && !proposals.length) setOpen(false);
    }, [open, proposals.length]);

    if (!proposals.length) return null;

    const decide = async (proposal, apply) => {
        setBusy(`${proposal.id}-${apply ? "apply" : "reject"}`);
        setErrors((current) => ({ ...current, [proposal.id]: null }));
        try {
            if (apply) await automationServices.applyRuleChangeProposal(proposal.id);
            else await automationServices.rejectRuleChangeProposal(proposal.id);
            setProposals((current) => current.filter((candidate) => candidate.id !== proposal.id));
            if (apply) onApplied?.();
        } catch (requestError) {
            setErrors((current) => ({ ...current, [proposal.id]: requestErrors(requestError, "No se pudo completar.") }));
            // Una propuesta que ya no calza con la regla queda descartada en el servidor.
            if (requestError?.response?.status === 409) load();
        } finally {
            setBusy(null);
        }
    };

    const count = proposals.length;

    return (
        <>
            <Alert
                severity="warning"
                sx={{ mb: 2, alignItems: "center" }}
                action={<Button color="inherit" size="small" onClick={() => setOpen(true)}>Revisar</Button>}>
                {count === 1
                    ? "Un asistente propuso un cambio que baja la protección de una regla. No rige hasta que lo apruebes."
                    : `Hay ${count} cambios propuestos por asistentes que bajan la protección de reglas. No rigen hasta que los apruebes.`}
            </Alert>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cambios propuestos</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} divider={<Divider flexItem />}>
                        {proposals.map((proposal) => (
                            <Box key={proposal.id}>
                                <Typography variant="h6" sx={{ m: 0 }}>{proposal.rule.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {proposal.proposed_by} vía {proposal.client || "un token"} ({proposal.channel === "mcp" ? "MCP" : "API"}) · {moment(proposal.created_at).format("DD-MM-YYYY HH:mm")}
                                </Typography>
                                <Stack spacing={0.5} sx={{ mt: 1 }}>
                                    {proposal.changes.map((change) => (
                                        <Stack key={change.text} direction="row" spacing={1} alignItems="flex-start">
                                            {change.reduces_protection
                                                ? <WarningAmberOutlinedIcon fontSize="small" color="warning" sx={{ mt: 0.15 }} />
                                                : <AddCircleOutlineOutlinedIcon fontSize="small" color="disabled" sx={{ mt: 0.15 }} />}
                                            <Typography variant="body2">{change.text}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                                {!!errors[proposal.id] && <Alert severity="error" sx={{ mt: 1 }}>{errors[proposal.id]}</Alert>}
                                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                                    <LoadingButton size="small" loading={busy === `${proposal.id}-reject`} disabled={!!busy} onClick={() => decide(proposal, false)}>
                                        Rechazar
                                    </LoadingButton>
                                    <LoadingButton size="small" variant="contained" color="warning" loading={busy === `${proposal.id}-apply`} disabled={!!busy}
                                                   onClick={() => decide(proposal, true)}>
                                        Aplicar cambio
                                    </LoadingButton>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions><Button onClick={() => setOpen(false)}>Cerrar</Button></DialogActions>
            </Dialog>
        </>
    );
};

export default RuleChangeProposals;
